import * as TabsPrimitive from '@radix-ui/react-tabs';
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from 'react';

import { cn } from '../../lib/utils';

export const Tabs = TabsPrimitive.Root;

export const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

/**
 * The compact (32px) rail, for secondary tab strips such as Edit/Preview
 * toggles and review sub-tabs.
 *
 * It is a CONSTANT, not five inline `"h-8"` strings, because shrinking the
 * rail without shrinking the inset is a real defect and it shipped five times:
 * the base rail is `h-10 p-1`, so `h-8` alone leaves a 4px inset around a
 * 28px trigger inside a 32px box — the trigger overflows the padding box and
 * the visible clearance reads 4px on the left and 2px above/below. Founder,
 * 2026-07-19, about the card tab strip: "the gap left of the active pill is
 * not the same as the gap above and below it".
 *
 * `p-0.5` sets the intended inset. The geometry harness checks that this
 * compact variant does not clip; it does not pin exact pixel clearances.
 */
export const COMPACT_TABS_LIST = 'h-8 p-0.5';

export const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    // touch-action: manipulation kills the iOS 300ms tap-delay that
    // sits between touchend and synthesised mousedown — Radix Tabs
    // activates on onMouseDown, so without this the tap appears to
    // "do nothing" for ~300ms and the user taps again. Wave-D2
    // (2026-05-25, founder report of 2-3 taps to switch tabs).
    // `select-none` keeps fast taps from accidentally selecting the
    // label text, which on iOS can cancel the click outright.
    className={cn(
      'inline-flex touch-manipulation items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-colors select-none',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50',
      'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      // [hidden]:!hidden — radix sets the `hidden` HTML attribute on
      // inactive panels, but author-CSS `display:flex` from `flex`
      // utility (added by callers like CardDetailSlideOver) overrides
      // the user-agent default `display:none` for [hidden]. Without
      // this `!important` override, inactive panels stay flex and
      // each take a slice of the parent flex column — squeezing the
      // active panel down to ~1/N of its rightful height (the
      // "history cropped" / "terminal short" bug).
      '[&[hidden]]:!hidden',
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
